import {
  ensureEvenDimension,
  getCappedThumbnailSize,
  MEDIA_UPLOAD_THUMBNAIL_MAX_EDGE_PX,
  MEDIA_UPLOAD_VIDEO_THUMBNAIL_CONTENT_TYPE,
  type MediaUploadThumbnailResult,
} from '@cartwhl/common/utils/mediaUploadThumbnail';

import { loadFfmpeg } from '../ffmpeg/loadFfmpeg';

const VIDEO_THUMBNAIL_MAX_DURATION_SECONDS = 15;
const INPUT_FILENAME = 'media_upload_source';
const OUTPUT_FILENAME = 'thumbnail.mp4';

type VideoDimensions = {
  width: number;
  height: number;
  duration: number;
};

async function readVideoDimensions(file: File): Promise<VideoDimensions> {
  const objectUrl = URL.createObjectURL(file);

  try {
    return await new Promise<VideoDimensions>((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          duration: Number.isFinite(video.duration) ? video.duration : 0,
        });
      };

      video.onerror = () => {
        reject(new Error('Failed to read video metadata'));
      };

      video.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function getScaledEvenDimensions(width: number, height: number) {
  const capped = getCappedThumbnailSize(
    width,
    height,
    MEDIA_UPLOAD_THUMBNAIL_MAX_EDGE_PX,
  );

  return {
    width: ensureEvenDimension(capped.width),
    height: ensureEvenDimension(capped.height),
  };
}

async function deleteFfmpegFile(
  ffmpeg: Awaited<ReturnType<typeof loadFfmpeg>>,
  filename: string,
) {
  try {
    await ffmpeg.deleteFile(filename);
  } catch {
    // Ignore missing temp files.
  }
}

export async function generateVideoUploadThumbnail(
  file: File,
  signal?: AbortSignal,
): Promise<MediaUploadThumbnailResult> {
  if (signal?.aborted) {
    throw new DOMException('Thumbnail generation aborted', 'AbortError');
  }

  const { width, height, duration } = await readVideoDimensions(file);
  if (width <= 0 || height <= 0) {
    throw new Error('Video has invalid dimensions');
  }

  const { width: targetWidth, height: targetHeight } = getScaledEvenDimensions(
    width,
    height,
  );
  const clipDuration = Math.min(
    VIDEO_THUMBNAIL_MAX_DURATION_SECONDS,
    duration > 0 ? duration : VIDEO_THUMBNAIL_MAX_DURATION_SECONDS,
  );

  const ffmpeg = await loadFfmpeg();
  if (signal?.aborted) {
    throw new DOMException('Thumbnail generation aborted', 'AbortError');
  }

  await deleteFfmpegFile(ffmpeg, INPUT_FILENAME);
  await deleteFfmpegFile(ffmpeg, OUTPUT_FILENAME);

  const inputBuffer = await file.arrayBuffer();
  if (signal?.aborted) {
    throw new DOMException('Thumbnail generation aborted', 'AbortError');
  }

  await ffmpeg.writeFile(INPUT_FILENAME, new Uint8Array(inputBuffer));

  try {
    await ffmpeg.exec([
      '-i',
      INPUT_FILENAME,
      '-t',
      `${clipDuration}`,
      '-vf',
      `scale=${targetWidth}:${targetHeight}`,
      '-an',
      '-c:v',
      'libx264',
      '-preset',
      'ultrafast',
      '-crf',
      '28',
      '-movflags',
      '+faststart',
      '-pix_fmt',
      'yuv420p',
      OUTPUT_FILENAME,
    ]);

    if (signal?.aborted) {
      throw new DOMException('Thumbnail generation aborted', 'AbortError');
    }

    const outputData = await ffmpeg.readFile(OUTPUT_FILENAME);
    if (typeof outputData === 'string') {
      throw new Error('Video thumbnail encode produced unexpected output');
    }

    return {
      blob: new Blob([outputData as BlobPart], {
        type: MEDIA_UPLOAD_VIDEO_THUMBNAIL_CONTENT_TYPE,
      }),
      contentType: MEDIA_UPLOAD_VIDEO_THUMBNAIL_CONTENT_TYPE,
    };
  } finally {
    await deleteFfmpegFile(ffmpeg, INPUT_FILENAME);
    await deleteFfmpegFile(ffmpeg, OUTPUT_FILENAME);
  }
}
